import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import {
  applyPickupStoreLink,
  PICKUP_STORE_PARAM,
} from '~/services/PickupStoreLink';
import { pickupStoreLinkState } from '~/services/pickupStoreLinkState';
import { mainStore } from '~/stores';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

/**
 * The URL as it stands right now, minus the store param.
 *
 * Read from `window.location` rather than the hook's `searchParams`: several
 * requests separate the render this was set up in from the moment it runs, and
 * writing that render's query string back would undo whatever else touched it
 * meanwhile. The hash rides along because consent.html forwards it through the
 * age gate on purpose, and a bare `?...` navigation would drop it.
 */
function locationWithoutStoreParam(): { search: string; hash: string } {
  const params = new URLSearchParams(window.location.search);
  params.delete(PICKUP_STORE_PARAM);
  const search = params.toString();

  return { search: search ? `?${search}` : '', hash: window.location.hash };
}

/**
 * Honours the in-store QR link: `?pickup-store=<warehouse code>` selects that
 * store as the pickup address before anything can ask the customer for one.
 *
 * Belongs in App rather than GlobalHooks so it keeps running while the page
 * content is hidden — a hook that unmounted mid-flight would never clear the
 * pending flag, and the storefront would stay hidden for good.
 */
export function usePickupStoreLink(): void {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paramCode = searchParams.get(PICKUP_STORE_PARAM);

  /**
   * The code to act on, kept past the URL that carried it.
   *
   * On a first RN launch App redirects to /onboarding with `replace`, dropping
   * the query string — before the stores have finished rehydrating, and so
   * before this hook is allowed to commit anything. Reading the live param at
   * that point would find nothing and the scan would be silently lost.
   */
  const pendingCodeRef = useRef<string | null>(null);
  /**
   * Codes that were honoured, so a second link in the same session still works
   * while the one already applied is not applied twice.
   *
   * Only successes go in. A link that failed on a flaky network is worth
   * another try — recording it here would make a re-scan of the same QR code
   * do nothing at all for the rest of the session.
   */
  const appliedCodesRef = useRef<Set<string>>(new Set());
  /** The code being resolved right now; blocks a second, racing commit. */
  const inFlightRef = useRef<string | null>(null);

  // Layout phase: the mandatory address gate is a passive effect in the same
  // component, so suppressing it after the commit would let the modal open for
  // a frame first. Never runs on the server, which is what keeps this write out
  // of the SSR server's shared module state.
  useIsomorphicLayoutEffect(() => {
    if (!company.hasPickupStoreLink || !paramCode) {
      return;
    }
    if (appliedCodesRef.current.has(paramCode) || inFlightRef.current) {
      return;
    }

    pendingCodeRef.current = paramCode;
    pickupStoreLinkState.setPending(true);
    pickupStoreLinkState.setBlocking(true);
  }, [paramCode]);

  useEffect(() => {
    const code = pendingCodeRef.current;

    // Persisted stores rehydrate asynchronously; committing before they land
    // lets the restored address overwrite the store just selected.
    if (!code || inFlightRef.current || !mainStore.isAllStoresSynchronized) {
      return;
    }

    inFlightRef.current = code;
    pendingCodeRef.current = null;

    void applyPickupStoreLink(code)
      .catch(() => false)
      .then((applied) => {
        if (applied) {
          appliedCodesRef.current.add(code);
        }
      })
      .finally(() => {
        inFlightRef.current = null;
        pickupStoreLinkState.setPending(false);
        // Dropping the param is also what re-arms the address gate: its effect
        // depends on the search params, so a link that could not be honoured
        // has to change them for the normal flow to take over.
        navigate(locationWithoutStoreParam(), { replace: true });
      });
  }, [paramCode, navigate, mainStore.isAllStoresSynchronized]);
}
